import { useCallback, useState } from 'react'
import { BoardRail, BoardColumn, useBoardDrag, Avatar, Chip, Button } from '../ds/index.js'
import BoardColumnMenu from '../components/BoardColumnMenu.jsx'
import CardMoveMenu from '../components/CardMoveMenu.jsx'
import { fmtDate, fmtMoney, tsToDate, canDeleteStage } from '../lib/format.js'

const BOARD_TITLE = { advisory: 'Advisory', speaking: 'Speaking', pr: 'PR & Media' }
const BOARD_BLURB = {
  advisory: 'Advisory engagements — retainers, Signal Audits and consulting work.',
  speaking: 'Speaking pipeline — from submitted to past.',
  pr: 'PR & media pipeline — from submitted to past.',
}
const FEE_STATUS_LABEL = { offered: 'Offered', confirmed: 'Confirmed', paid: 'Paid' }

export default function EngagementsView({ board, engagements = [], allEngagements = [], stages = [], usersById = {}, onOpen, onCreate, onMove, onCreateStage, onUpdateStage, onDeleteStage }) {
  // allEngagements: unfiltered engagements list; feeds the stage-delete guard so an engagement
  // hidden by an active owner/search filter still blocks deletion of the stage it belongs to.
  const [menuOpen, setMenuOpen] = useState(null)
  const [moveOpen, setMoveOpen] = useState(null)
  const [addingStage, setAddingStage] = useState(false)
  const [stageDraft, setStageDraft] = useState('')

  const submitAddStage = () => {
    const trimmed = stageDraft.trim()
    if (trimmed) {
      const maxOrder = stages.length ? Math.max(...stages.map((s) => s.order)) : -1
      onCreateStage({ name: trimmed, order: maxOrder + 1, dot: '#7A7568', color: '#7A7568', done: false })
    }
    setStageDraft(''); setAddingStage(false)
  }

  const sortedStages = [...stages].sort((a, b) => a.order - b.order)

  const onDrop = useCallback((engId, stageId, index) => onMove(engId, stageId, index), [onMove])
  const orderSignature = sortedStages
    .flatMap((s) => engagements.filter((e) => e.stage === s.id).sort((a, b) => a.order - b.order))
    .map((e) => `${e.id}:${e.stage}:${e.order}`).join('|')
  const { containerRef, getCardHandlers, getColProps, draggingId, wasDragging } = useBoardDrag({ onDrop, orderSignature })

  const moveStage = (idx, dir) => {
    const reordered = [...sortedStages]
    const [moved] = reordered.splice(idx, 1)
    reordered.splice(idx + dir, 0, moved)
    reordered.forEach((s, i) => { if (s.order !== i) onUpdateStage(s.id, { order: i }) })
  }

  return (
    <>
      <div className="eyebrow" style={{ marginBottom: 12 }}><span className="tag-label" style={{ color: 'var(--color-gold)' }}>Engagements</span></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 className="font-display" style={{ fontSize: 42, margin: '0 0 4px', lineHeight: 0.95, textTransform: 'uppercase', color: 'var(--color-white)' }}>{BOARD_TITLE[board] || 'Engagements'}</h1>
        <div style={{ flex: 1 }} />
        <button onClick={() => onCreate()} className="btn btn-gold" style={{ fontSize: 12, padding: '9px 16px' }}>+ Add engagement</button>
      </div>
      <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--color-muted)' }}>{BOARD_BLURB[board] || ''}</p>

      <BoardRail innerRef={containerRef}>
        {sortedStages.map((s, idx) => {
          const cards = engagements.filter((e) => e.stage === s.id).sort((a, b) => a.order - b.order)
          return (
            <BoardColumn
              key={s.id}
              title={s.name}
              count={cards.length}
              accent={s.dot}
              {...getColProps(s.id)}
              actions={
                <BoardColumnMenu
                  open={menuOpen === s.id}
                  onToggle={() => setMenuOpen((v) => (v === s.id ? null : s.id))}
                  onClose={() => setMenuOpen(null)}
                  name={s.name}
                  renameValue={s.name}
                  onRename={(name) => onUpdateStage(s.id, { name })}
                  onRecolor={(c) => onUpdateStage(s.id, { color: c, dot: c })}
                  recolorValue={s.color || s.dot}
                  onMoveLeft={() => moveStage(idx, -1)}
                  onMoveRight={() => moveStage(idx, 1)}
                  canMoveLeft={idx > 0}
                  canMoveRight={idx < sortedStages.length - 1}
                  canDelete={canDeleteStage(s.id, allEngagements, stages)}
                  onDelete={() => onDeleteStage(s.id)}
                  confirmNote="Move its cards out first (or keep at least one stage)."
                />
              }
            >
              {cards.map((e) => {
                const owner = usersById[e.ownerUid]
                const date = fmtDate(tsToDate(e.date))
                const dragging = draggingId === e.id
                return (
                  <div
                    key={e.id}
                    {...getCardHandlers(e.id)}
                    data-dragging={dragging ? 'true' : 'false'}
                    onClick={() => { if (!wasDragging()) onOpen(e.id) }}
                    style={{ border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-lg)', background: 'var(--surface-card)', padding: '12px 13px', cursor: 'grab', transition: 'border-color .12s' }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.borderColor = 'var(--color-gold)')}
                    onMouseLeave={(ev) => (ev.currentTarget.style.borderColor = 'var(--color-divider-dark)')}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                      <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--color-white)', lineHeight: 1.4 }}>{e.title}</div>
                      <CardMoveMenu
                        open={moveOpen === e.id}
                        onToggle={() => setMoveOpen((v) => (v === e.id ? null : e.id))}
                        onClose={() => setMoveOpen(null)}
                        columns={sortedStages.map((st) => ({ id: st.id, name: st.name, dot: st.dot }))}
                        currentId={e.stage}
                        onMove={(destId) => onMove(e.id, destId)}
                      />
                    </div>
                    {e.org && <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 8 }}>{e.org}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Avatar user={owner} size={22} />
                      {date && <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)' }}>{date}</span>}
                      <div style={{ flex: 1 }} />
                      {typeof e.fee === 'number' && <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-gold)' }}>{fmtMoney(e.fee)}</span>}
                      {e.feeStatus && <Chip variant={e.feeStatus === 'paid' ? 'teal' : 'gold'}>{FEE_STATUS_LABEL[e.feeStatus]}</Chip>}
                    </div>
                  </div>
                )
              })}
              {cards.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '6px 2px' }}>Empty</div>}
            </BoardColumn>
          )
        })}

        {/* Ghost "Add stage" column */}
        <div
          style={{
            flex: '0 0 auto',
            minWidth: 220,
            border: '1px dashed var(--color-divider-dark)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '13px 14px',
            opacity: 0.55,
            transition: 'opacity .15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.55')}
        >
          {addingStage ? (
            <input
              value={stageDraft}
              onChange={(e) => setStageDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitAddStage(); if (e.key === 'Escape') { setAddingStage(false); setStageDraft('') } }}
              onBlur={submitAddStage}
              autoFocus
              placeholder="Stage name — Enter to add"
              className="cn-input"
              style={{ fontSize: 13, padding: '9px 11px', width: '100%', boxSizing: 'border-box' }}
            />
          ) : (
            <Button variant="ghost" onClick={() => { setAddingStage(true); setStageDraft('') }} style={{ fontSize: 13, padding: '4px 0' }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add stage
            </Button>
          )}
        </div>
      </BoardRail>
    </>
  )
}
